"use client";

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { getWorkspaceEntitlementPolicy } from "@/lib/api/endpoints/entitlements";
import { useAuthStore } from "@/modules/auth/store";

function toDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function addMonths(value: Date, months: number): Date {
  const year = value.getFullYear();
  const month = value.getMonth() + months;
  const day = value.getDate();
  const result = new Date(year, month, 1);
  const lastDay = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
  result.setDate(Math.min(day, lastDay));
  return result;
}

export function useWarrantyPreview(storedWarrantyEnd?: string | null) {
  const tenantId = useAuthStore((s) => s.tenantId);
  const normalizedStoredWarrantyEnd = storedWarrantyEnd?.trim() || "";

  const policyQuery = useQuery({
    queryKey: ["entitlements", "workspace", tenantId ?? "active", "device-warranty-preview"],
    queryFn: () => getWorkspaceEntitlementPolicy(tenantId),
    enabled: Boolean(tenantId) && !normalizedStoredWarrantyEnd,
    staleTime: 60_000,
  });

  const previewWarrantyEnd = useMemo(() => {
    if (normalizedStoredWarrantyEnd) {
      return normalizedStoredWarrantyEnd;
    }
    const months = policyQuery.data?.default_warranty_months;
    if (!Number.isFinite(months)) {
      return "";
    }
    return toDateInputValue(addMonths(new Date(), Number(months)));
  }, [normalizedStoredWarrantyEnd, policyQuery.data?.default_warranty_months]);

  return {
    warrantyEnd: previewWarrantyEnd,
    isLoading: policyQuery.isLoading,
    defaultWarrantyMonths: policyQuery.data?.default_warranty_months ?? null,
  };
}
